import { createUseStyles } from 'react-jss';
import { WrapWithCardOnLargerScreen } from '../components/WrapWithCardOnLargerScreen';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { InnerhivePage } from '../InnerhivePage';
import { useState } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { useTrackOnVisible } from '../metrics/useTrackOnVisible';
import { HomePageMenu } from './HomePageMenu';
import { useSubscription } from '@meraki-internal/state';
import { MDTabs } from '../components/MDTabs';
import { OrganizationClients } from '../organizations/OrganizationClients';
import { OrganizationTeam } from '../organizations/OrganizationTeam';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { HomePageBanner } from './HomePageBanner';
import { CaregiverTools } from './CaregiverTools';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { initCap } from '../utils/format';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { Recordings } from './Recordings';
import { RecordingState } from '../recording/RecordingState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { RecordingEmptyState } from '../recording/RecordingEmptyState';
import { InsightsHomeSection } from '../insights/InsightsHomeSection';
import { CheckinCard } from '../journal/JournalCard';
import { BeaSonarButton } from '../helper-bee/BeaSonarButton';
import { HelperBeeCard } from '../helper-bee/HelperBeeCard';
import { mediaMinWidth } from '../theme/utils';

const useStyles = createUseStyles({
    page: {
        width: '100%'
    },
    // Anchor the care-recipient switcher as a floating card over the bottom of the video hero,
    // straddling the seam into the content, on every screen size.
    heroWrap: {
        position: 'relative'
    },
    switcherOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 6,
        zIndex: 5,
        // above md the content card rides 30px up over the hero (WrapWithCardOnLargerScreen's
        // marginTop -30), so lift the switcher fully onto the video instead of onto the card
        [mediaMinWidth.md]: {
            bottom: 44
        }
    },
    // Mirror the content column's geometry exactly (maxWidth + centering + the content padding and
    // ion-card margin below) so the switcher's left edge lines up with the cards to the pixel:
    // 20px on small screens (10 content padding + 10 card margin); 10px above md, where
    // WrapWithCardOnLargerScreen's outer card edge (margin 10) is the visible alignment target.
    switcherColumn: {
        maxWidth: 650,
        margin: '0 auto',
        padding: '0 20px',
        [mediaMinWidth.md]: {
            padding: '0 10px'
        }
    },
    switcherFit: {
        display: 'inline-block'
    },
    // Same vertical placement as BasicHeader's beaButtonWrapper; horizontally the button hugs the
    // content column's right edge (same column band as the switcher) instead of the viewport's.
    // pointer-events pass through the band so only the button itself is tappable.
    beaButtonBand: {
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 22px)',
        left: 0,
        right: 0,
        zIndex: 100,
        pointerEvents: 'none'
    },
    beaButtonEnd: {
        display: 'flex',
        justifyContent: 'flex-end',
        '& > *': {
            pointerEvents: 'auto'
        }
    },
    content: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: 650,
        margin: 'auto'
    }
});

export const CaregiverHomePage: React.FC<{ careMapId?: string;}> = ({ careMapId }) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const orgsState = useInstance(OrganizationsState);
    useSubscription(() => orgsState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    // Wait until the page is actually visible (no Welcome slides / Intro overlay
    // on top) before firing this mount-time event.
    useTrackOnVisible(`Caregiver's Corner Opened`);

    // active caremap is needed for both onboarding and checkins
    const activeCareMapId = careMapId || careMapState.getMostRecentCaremap()?.careMapId;

    // Intro card vs tabs: only show intro card once notes have loaded and confirm no recordings.
    // Default to tabs while loading so existing users don't see a flash of the intro card.
    let isIntroFlow = false;
    try {
        const notesModel = notesModelFactory.forActiveCaremap();
        if (notesModel.hasLoaded) {
            isIntroFlow = recordingState.shouldUseIntroFlow();
        }
    } catch {
        // No active caremap yet — default to intro card only if there's genuinely no caremap
        isIntroFlow = !activeCareMapId;
    }

    const orgId = (orgsState.isOrgTeamMember() ? orgsState.getMostRecentOrganization()?.orgId : undefined);
    const [orgTab, setOrgTab] = useState<'clients' | 'team' | 'tools'>('clients');
    const onChangeOrgTab = (tab: typeof orgTab) => {
        setOrgTab(tab);
        mixpanel.track(`${initCap(tab)} Tab Opened`);
    };

    return (
        <ActiveCareMapProviderV2 careMapId={activeCareMapId}>
            <InnerhivePage hideAppHeader backgroundColor="light">
                <div className={classes.page}>

                    <HomePageMenu />
                    <div className={classes.heroWrap}>
                        <HomePageBanner />
                        <div className={classes.beaButtonBand}>
                            <div className={`${classes.switcherColumn} ${classes.beaButtonEnd}`}>
                                <BeaSonarButton trackingFrom="Welcome" data-id="bea-header-button" />
                            </div>
                        </div>
                        {!orgId && (
                            <div className={classes.switcherOverlay}>
                                <div className={classes.switcherColumn}>
                                    <div className={classes.switcherFit}>
                                        <CareRecipientSwitcher onImagery />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                        <div className={classes.content}>
                            <WrapWithCardOnLargerScreen marginTop={-30} marginBottom={30}>
                                <div style={{minHeight: 400, padding: 10}}>
                                    {!orgId ? (
                                        isIntroFlow ? (
                                            <RecordingEmptyState />
                                        ) : (
                                            <>
                                                <InsightsHomeSection />

                                                <div data-id="home-recording-list" style={{marginBottom: 20}}>
                                                    <Recordings hideSwitcher />
                                                </div>

                                                <div style={{marginBottom: 20}}>
                                                    <HelperBeeCard />
                                                </div>

                                                <CheckinCard />
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <MDTabs
                                                value={orgTab}
                                                onChange={onChangeOrgTab}
                                                buttons={[
                                                    { label: 'Clients', value: 'clients' },
                                                    { label: 'Team', value: 'team' },
                                                    { label: 'Tools', value: 'tools' }
                                                ]}
                                            />

                                            <div style={{paddingTop: 10}}>
                                                {orgTab === 'clients' && orgId &&
                                                    <OrganizationClients orgId={orgId} />
                                                }
                                                {orgTab === 'team' && orgId &&
                                                    <OrganizationTeam orgId={orgId} />
                                                }
                                                {orgTab === 'tools' &&
                                                    <>
                                                        <div style={{padding: 10}}>
                                                            <CareRecipientSwitcher
                                                                onClick={() => setOrgTab('clients')}
                                                            />
                                                        </div>
                                                        <CaregiverTools />
                                                    </>
                                                }
                                            </div>
                                        </>
                                    )}

                                </div>
                            </WrapWithCardOnLargerScreen>
                        </div>

                    </div>
                </InnerhivePage>
        </ActiveCareMapProviderV2>
    );
};
